/**
 * Subject Detail — "Concomitant medications" section (2026-10-08).
 *
 * From the CTMS schema spec sheet 2 (`subject_concom_medication`).
 * `whodrug_code` is optional on entry for the same reason MedDRA coding is
 * on medical history: coding happens later and must not block the site.
 *
 * "Ongoing" and an end date are mutually exclusive — the server rejects the
 * pair, so the end-date input is disabled while Ongoing is ticked rather
 * than letting the user fill in something that will be refused.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Pill, Plus, Trash2, X } from 'lucide-react';
import { subjectClinical, type ConmedRow } from '@/api/client';
import { fmtDate } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

const WRITE_ROLES = new Set([
  'coordinator',
  'principal_investigator',
  'sub_investigator',
  'clinical_nurse',
]);

const EMPTY = {
  medication_name_verbatim: '',
  indication_text: '',
  dose_amount: '',
  frequency_route: '',
  whodrug_code: '',
  start_date: '',
  end_date: '',
  is_ongoing: true,
};

export function ConmedsSection({ subjectId }: { subjectId: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.has(user?.role ?? '');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const listQ = useQuery({
    queryKey: ['subject-conmeds', subjectId],
    queryFn: () => subjectClinical.listConmeds(subjectId),
    staleTime: 30_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['subject-conmeds', subjectId] });
  };

  const createM = useMutation({
    mutationFn: () =>
      subjectClinical.createConmed(subjectId, {
        medication_name_verbatim: form.medication_name_verbatim.trim(),
        indication_text: form.indication_text.trim(),
        dose_amount: form.dose_amount.trim(),
        frequency_route: form.frequency_route.trim(),
        is_ongoing: form.is_ongoing,
        ...(form.whodrug_code.trim() ? { whodrug_code: form.whodrug_code.trim() } : {}),
        ...(form.start_date ? { start_date: form.start_date } : {}),
        ...(!form.is_ongoing && form.end_date ? { end_date: form.end_date } : {}),
      }),
    onSuccess: () => {
      toast.success('Medication recorded.');
      setAdding(false);
      setForm(EMPTY);
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not record the medication.')),
  });

  const deleteM = useMutation({
    mutationFn: (conmedId: string) => subjectClinical.deleteConmed(subjectId, conmedId),
    onSuccess: () => {
      toast.success('Medication removed.');
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not remove the medication.')),
  });

  const rows = listQ.data?.rows ?? [];

  return (
    <section className="rounded border border-surface-border bg-white">
      <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <Pill className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Concomitant medications</h2>
          {rows.length > 0 && <span className="text-xs text-ink-muted">({rows.length})</span>}
        </div>
        {canWrite && !adding && (
          <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add medication
          </button>
        )}
      </header>

      {adding && (
        <form
          className="px-4 py-3 border-b border-surface-border bg-surface-muted/30 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createM.mutate();
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-ink-muted">
              Medication <span className="text-danger-600">*</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.medication_name_verbatim}
                maxLength={255}
                required
                onChange={(e) => setForm({ ...form, medication_name_verbatim: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-muted">
              Indication <span className="text-danger-600">*</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.indication_text}
                required
                onChange={(e) => setForm({ ...form, indication_text: e.target.value })}
                placeholder="Why the subject is taking it"
              />
            </label>
            <label className="text-xs text-ink-muted">
              Dose <span className="text-danger-600">*</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.dose_amount}
                required
                maxLength={100}
                onChange={(e) => setForm({ ...form, dose_amount: e.target.value })}
                placeholder="e.g. 10 mg"
              />
            </label>
            <label className="text-xs text-ink-muted">
              Frequency / route <span className="text-danger-600">*</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.frequency_route}
                required
                maxLength={100}
                onChange={(e) => setForm({ ...form, frequency_route: e.target.value })}
                placeholder="e.g. BID oral"
              />
            </label>
            <label className="text-xs text-ink-muted">
              Start date
              <input
                type="date"
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-muted">
              End date
              <input
                type="date"
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink disabled:bg-surface-muted disabled:text-ink-muted"
                value={form.is_ongoing ? '' : form.end_date}
                disabled={form.is_ongoing}
                title={form.is_ongoing ? 'An ongoing medication cannot have an end date.' : undefined}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-muted md:col-span-2">
              WHODrug code <span className="text-ink-muted">(optional — added when coded)</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink font-mono"
                value={form.whodrug_code}
                maxLength={50}
                onChange={(e) => setForm({ ...form, whodrug_code: e.target.value })}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink md:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-surface-border accent-navy-600"
                checked={form.is_ongoing}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_ongoing: e.target.checked,
                    end_date: e.target.checked ? '' : form.end_date,
                  })
                }
              />
              Ongoing — the subject is still taking this
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary" disabled={createM.isPending}>
              {createM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save medication
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setAdding(false);
                setForm(EMPTY);
              }}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        </form>
      )}

      {listQ.isLoading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-4 w-4 animate-spin mx-auto text-ink-muted" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-ink-muted">
          No concomitant medications recorded for this subject.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
              <tr>
                <th className="px-4 py-2">Medication</th>
                <th className="px-4 py-2">Indication</th>
                <th className="px-4 py-2">Dose</th>
                <th className="px-4 py-2">Freq / route</th>
                <th className="px-4 py-2">Start</th>
                <th className="px-4 py-2">End</th>
                <th className="px-4 py-2">WHODrug</th>
                {canWrite && <th className="px-4 py-2 w-10" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r: ConmedRow) => (
                <tr key={r.conmed_id} className="border-b border-surface-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">{r.medication_name_verbatim}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{r.indication_text}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{r.dose_amount}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{r.frequency_route}</td>
                  <td className="px-4 py-2.5 text-ink-muted">
                    {r.start_date ? fmtDate(r.start_date) : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.is_ongoing ? (
                      <span className="px-1.5 py-0.5 rounded border text-[11px] font-medium bg-amber-50 text-amber-700 border-amber-200">
                        Ongoing
                      </span>
                    ) : (
                      <span className="text-ink-muted">{r.end_date ? fmtDate(r.end_date) : '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted font-mono text-xs">
                    {r.whodrug_code || '—'}
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        title="Remove medication"
                        className="text-ink-muted hover:text-danger-600"
                        disabled={deleteM.isPending}
                        onClick={() => deleteM.mutate(r.conmed_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
